import { useEffect, useState } from 'react';
import { Moon, Sun } from 'lucide-react';
import { NavLink, Outlet } from 'react-router-dom';
import { Seal } from './Seal';
import { useTheme } from '../lib/theme';
import { getMe, type Me } from '../lib/session';

const NAV = [
  { to: '/', label: 'Home', end: true },
  { to: '/manifest', label: 'Manifest', end: false },
  { to: '/principles', label: 'Principles', end: false },
  { to: '/library', label: 'Library', end: false },
  { to: '/apply', label: 'Apply', end: false },
  { to: '/donate', label: 'Donate', end: false },
];

/** Nav links shown only to an authenticated caller, gated by role rank. Because these render *only*
 *  when signed in, the logged-out chrome (and its visual-regression baselines) is unchanged. */
const RANK: Record<Me['role'], number> = { member: 1, board: 2, admin: 3 };
const AUTHED_NAV: { to: string; label: string; min: Me['role'] }[] = [
  { to: '/meetings', label: 'Meetings', min: 'member' },
  { to: '/finances', label: 'Finances', min: 'member' },
  { to: '/board/queue', label: 'Applications', min: 'board' },
  { to: '/board/donations', label: 'Donations', min: 'board' },
  { to: '/members', label: 'Members', min: 'board' },
  { to: '/profile', label: 'Profile', min: 'member' },
];

/**
 * Public site chrome: a clean top bar (seal at left, page nav, theme toggle) wrapping the routed page,
 * with a footer. Mirrors the header/footer already in the scaffold's App.tsx (MASTER.md: not floating,
 * not loud). All routes render inside <Outlet/>.
 */
export function SiteLayout() {
  const [theme, toggleTheme] = useTheme();
  const nextIsDark = theme !== 'dark';
  const [me, setMe] = useState<Me | null>(null);

  useEffect(() => {
    let active = true;
    getMe().then((r) => {
      if (active && r.kind === 'ok') setMe(r.user);
    });
    return () => {
      active = false;
    };
  }, []);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    [
      'rounded-md px-2.5 py-1.5 font-sans text-sm transition-colors duration-150 ease-standard',
      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
      isActive ? 'text-ink underline decoration-accent decoration-2 underline-offset-8' : 'text-muted hover:text-ink',
    ].join(' ');

  return (
    <div className="flex min-h-full flex-col bg-bg text-ink">
      <header className="border-b border-border">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-4 px-6 py-4">
          <NavLink
            to="/"
            className="flex items-center gap-3 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <Seal className="h-8 w-8" />
            <span className="font-display text-sm font-semibold uppercase tracking-[0.2em] text-ink">
              Original Illuminati
            </span>
          </NavLink>

          <nav aria-label="Primary" className="flex items-center gap-1 sm:gap-2">
            {NAV.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className={linkClass}>
                {item.label}
              </NavLink>
            ))}
            {me &&
              AUTHED_NAV.filter((item) => RANK[me.role] >= RANK[item.min]).map((item) => (
                <NavLink key={item.to} to={item.to} className={linkClass}>
                  {item.label}
                </NavLink>
              ))}
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={nextIsDark ? 'Switch to dark theme' : 'Switch to light theme'}
              className="ml-1 inline-flex h-9 w-9 items-center justify-center rounded-md border border-border text-ink transition-colors duration-150 ease-standard hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              {nextIsDark ? <Moon aria-hidden className="h-4 w-4" /> : <Sun aria-hidden className="h-4 w-4" />}
            </button>
          </nav>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-border">
        <div className="mx-auto flex w-full max-w-7xl flex-col items-center gap-1 px-6 py-6 text-center font-sans text-xs text-muted">
          <p>A free, self-hosted, open platform for the association. Membership is free.</p>
          <p className="italic">Scientia et Caritas — Knowledge &amp; Compassion</p>
        </div>
      </footer>
    </div>
  );
}
